import { useEffect, useRef, useState } from 'react'
import { useThree, type ThreeEvent } from '@react-three/fiber'
import type { Group } from 'three'
import type { FerramentaOficinaCorte } from '../../dominio/didatico/oficinaCorte'
import { FerramentaHidraulica3D } from './FerramentaHidraulica3D'
import { poseAcessoPorta } from './poseAcessoPorta'
import { FerramentaManual3D } from './FerramentaManual3D'
import { TransicaoSuave } from '../componentes/TransicaoSuave'

interface Props {
  ferramenta: FerramentaOficinaCorte
  progresso: number
  posicao: [number, number, number]
  anguloGraus: number
  lado: 'esquerdo' | 'direito'
  /**
   * Giro atual da peça em torno do seu eixo, em radianos. A ferramenta
   * acompanha a porta que abre, em vez de continuar apontando para onde a
   * peça estava fechada.
   */
  giroPeca?: number
  /** Giro em X usado pela tampa traseira. */
  giroPecaX?: number
  /** Ponto do gesto no referencial do veículo; quem chama decide o que fazer com ele. */
  aoMover?: (ponto: [number, number, number]) => void
  /**
   * `vertical` arrasta no plano Y-Z da porta lateral (padrão). `horizontal`
   * arrasta no plano X-Z da tampa traseira — a peça não tem lado, e o gesto
   * precisa cobrir a largura da tampa em vez da altura da porta.
   */
  plano?: 'vertical' | 'horizontal'
  modoAlargador?: 'abrir' | 'fechar'
  /** Afastamento em metros ao longo da linha de aproximação (recuo pedagógico) */
  afastamento?: number
  /** Se o arraste manual por ponteiro deve ser ignorado (ex: modo player) */
  desabilitarArraste?: boolean
  /** Se a ferramenta deve ser posicionada paralela à lateral do veículo (ex.: pinçamento do para-lama) */
  orientacaoParalela?: boolean
  alturaEsquadria?: number
  comprimirMacaneta?: boolean
  /** 0 = garras no estado inicial (no ar), 1 = pose de trabalho. */
  preparo?: number
  estadoInicial?: 'aberto' | 'fechado' | null
  /** Zona atual: quando muda com a ferramenta em cena, ela desliza até a nova. */
  chaveTransicao?: string | null
  deslizar?: boolean
}

/**
 * Distância da origem de cada maquete até a sua ponta de trabalho, no eixo X
 * local (ver FerramentaHidraulica3D e FerramentaManual3D). É por esse ponto
 * que a ferramenta encosta na peça: as maquetes nascem centradas no corpo, e
 * usar a origem como ponto de aplicação deitava metade do equipamento dentro
 * da chapa, em vez de mostrar a ponta apoiada no marcador.
 *
 * Nas ferramentas de mandíbula o ponto não é a extremidade das lâminas e sim
 * o vão entre elas, onde a peça é de fato mordida — as lâminas fecham sobre o
 * marcador em vez de encostar nele de raspão.
 */
const DISTANCIA_DA_PONTA: Record<string, number> = {
  cortadorHidraulico: 0.62,
  alargador: 0.66,
  extensor: 0.62,
  hooligan: 0.72,
  puncaoVidro: 0.16,
}

/**
 * Inclinação de aproximação: a ponta fica no marcador e o corpo sobe para
 * fora da peça. Sem ela a ferramenta ficaria deitada rente à chapa — o corpo
 * tem raio maior que a folga do marcador e atravessaria a lataria.
 */
const INCLINACAO_APROXIMACAO_RAD = (20 * Math.PI) / 180

/** Ferramenta arrastável no plano da peça (porta ou tampa), com órbita suspensa durante o gesto. */
export function FerramentaPosicionavel3D({
  ferramenta,
  progresso,
  posicao,
  anguloGraus,
  lado,
  giroPeca = 0,
  giroPecaX = 0,
  aoMover,
  plano = 'vertical',
  modoAlargador = 'abrir',
  afastamento = 0,
  desabilitarArraste = false,
  orientacaoParalela = false,
  alturaEsquadria,
  comprimirMacaneta = false,
  preparo = 1,
  estadoInicial,
  chaveTransicao,
  deslizar = false,
}: Props) {
  const [arrastando, setArrastando] = useState(false)
  const raizRef = useRef<Group>(null)
  const controles = useThree((estado) => estado.controls) as { enabled?: boolean } | null

  useEffect(() => {
    if (controles) controles.enabled = !arrastando
    return () => { if (controles) controles.enabled = true }
  }, [arrastando, controles])

  useEffect(() => {
    if (!arrastando) return
    const soltar = () => setArrastando(false)
    window.addEventListener('pointerup', soltar)
    window.addEventListener('pointercancel', soltar)
    return () => {
      window.removeEventListener('pointerup', soltar)
      window.removeEventListener('pointercancel', soltar)
    }
  }, [arrastando])

  const mover = (evento: ThreeEvent<PointerEvent>) => {
    if (!arrastando || !aoMover) return
    evento.stopPropagation()
    const pontoLocal = raizRef.current?.worldToLocal(evento.point.clone()) ?? evento.point
    aoMover([pontoLocal.x, pontoLocal.y, pontoLocal.z])
  }

  const rotacaoTampa: [number, number, number] = [
    (16 * Math.PI) / 180 + giroPecaX,
    -Math.PI / 2,
    0,
  ]

  // Na orientação paralela (pinçamento do para-lama), a ferramenta entra de frente
  // para o veículo, posicionada acima do meio da roda dianteira (perfil.eixoDianteiroZ).
  // O corpo do alargador estende-se para a frente (+Z) sobre o para-choque/balanço dianteiro
  // com elevação angular (~12°) e leve saída lateral (~6°), enquanto as pontas mordem para
  // trás (-Z) entre o capô e a borda da caixa de roda. No ciclo de alívio (progresso > 0.5),
  // as pontas inclinam para a frente, alavancando e abrindo folga para as dobradiças na Coluna A.
  const inclinacaoAlavanca = modoAlargador === 'fechar' && progresso > 0.5
    ? ((progresso - 0.5) / 0.5) * 0.14
    : 0

  const anguloAfastamentoLateral = (lado === 'esquerdo' ? 1 : -1) * 0.10
  const rotacaoLateral: [number, number, number] = orientacaoParalela
    ? [
        -0.12,
        Math.PI / 2 + anguloAfastamentoLateral + giroPeca,
        0,
      ]
    : [
        (lado === 'esquerdo' ? 1 : -1) * anguloGraus * Math.PI / 180 + giroPecaX,
        (lado === 'esquerdo' ? -Math.PI / 2 : Math.PI / 2) + giroPeca,
        0,
      ]
  const poseAcesso = ferramenta === 'alargador' && (alturaEsquadria != null || comprimirMacaneta)
    ? poseAcessoPorta(progresso, alturaEsquadria) : null
  const rollTampa = plano === 'horizontal' ? ((anguloGraus - 90) * Math.PI) / 180 : 0

  return (
    <group ref={raizRef}>
      <group
        position={alturaEsquadria == null ? posicao : [posicao[0], posicao[1] + 0.03 * progresso, posicao[2]]}
        rotation={plano === 'horizontal' ? rotacaoTampa : rotacaoLateral}
        scale={0.72}
        onPointerDown={(evento) => {
          if (desabilitarArraste) return
          evento.stopPropagation()
          setArrastando(true)
        }}
      >
        {/* pivô na ponta: inclina a aproximação sem tirar a ponta do marcador */}
        <group
          rotation={plano === 'horizontal'
            ? [rollTampa, 0, 0]
            : orientacaoParalela
              ? [0, 0, (lado === 'esquerdo' ? 1 : -1) * inclinacaoAlavanca]
              : poseAcesso ? [0, 0, 0] : [0, -INCLINACAO_APROXIMACAO_RAD, 0]}
        >
          <group position={[-(poseAcesso?.distanciaPonta ?? DISTANCIA_DA_PONTA[ferramenta] ?? 0) - afastamento, 0, 0]}>
            <TransicaoSuave chave={chaveTransicao} ativa={deslizar}>
              {ferramenta === 'alargador' || ferramenta === 'cortadorHidraulico' || ferramenta === 'extensor'
                ? <FerramentaHidraulica3D ferramenta={ferramenta} progresso={progresso} ativa={progresso > 0} modoAlargador={modoAlargador}
                    anguloMandibula={poseAcesso?.anguloMandibula} preparo={preparo} estadoInicial={estadoInicial} />
                : ferramenta === 'hooligan' || ferramenta === 'puncaoVidro'
                  ? <FerramentaManual3D ferramenta={ferramenta} progresso={progresso} ativa={progresso > 0} />
                  : null}
            </TransicaoSuave>
          </group>
        </group>
      </group>

      {arrastando && (
        <mesh
          // o plano do gesto acompanha a peça: com a porta aberta, arrastar
          // num plano fixo empurraria a ferramenta para fora da superfície
          position={plano === 'horizontal' ? [0, posicao[1], 0] : [posicao[0], 1, 0]}
          rotation={plano === 'horizontal' ? [-Math.PI / 2 + giroPecaX, 0, 0] : [0, Math.PI / 2 + giroPeca, 0]}
          onPointerMove={mover}
          onPointerUp={() => setArrastando(false)}
        >
          <planeGeometry args={[8, 5]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
      )}
    </group>
  )
}
